import { Button, Form, FormInstance, Pagination, Spin, Tabs } from 'antd';
import { SvgCoppy } from '../../../components/@svg/SvgCheck';
import TableWrap from '../../../components/TableWrap';
import CustomSearchInput from '../../../components/input/CustomSearchInput';
import './index.scss';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import DatepickerCustom from '../../../components/DatepickerCustom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { QUERY_KEY, STATUS_ITEM_MANAGEMENT, TIME_FORMAT } from '../../../util/constants';
import { fileExportApi, storageItemApi } from '../../../apis';
import { CountForItemListResponse, StorageItem } from '../../../apis/client-axios';
import { formatDate, handleViewStatusItem, renderStatus } from '../../../util/helper';
import NotificationSuccess from '../../../components/notifications/NotificationSuccess';
import dayjs from 'dayjs';
import { NavigateFunction, useNavigate, useSearchParams } from 'react-router-dom';
import ButtonDetail from '../../../components/buttons/ButtonDetail';
import { ADMIN_ROUTE_PATH } from '../../../constants/router';
import { decode as decodeHtml } from 'he';
import CustomSelect from '../../../components/CustomSelect';
import { CheckIcon } from './@svg/tab';
import DowloadButton from '../../../components/buttons/DowloadButton';
import { saveAs } from 'file-saver';
import SvgFilterSort from '../../../components/@svg/SvgFilterSort';
import SvgArrowDown from '../../../components/@svg/SvgArrowDown';
import moment from 'moment';
import { AmericaTimezone } from '../../../util/function-util';
import CheckboxCustom from '../../../components/buttonCustom/CheckboxCustom';
import { CsvBulkRegisterModal } from './modals/CsvBulkRegisterModal';
import SvgImport from '../../../components/@svg/SvgImport';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';

type TColumnTable = {
  intl: IntlShape;
  tabActive: string;
  copyToClipboard: (value: string) => void;
  navigate: NavigateFunction;
  sort: TSort;
};

type TColumnSort = 'internalSku' | 'createdAt' | 'lastUpdate' | 'storageRequestShippedAt';
type TTypeSort = 'ASC' | 'DESC';
type TFilterSort = { columns: TColumnSort; sort: TTypeSort };
type TSort = {
  filterSort: TFilterSort;
  handleSort: (columns: TColumnSort) => void;
};

enum DataType {
  NEW_REQUEST_DATE = 'NEW_REQUEST_DATE',
  ARRIVAL_DATE = 'ARRIVAL_DATE',
  INSPECTION_DATE = 'INSPECTION_DATE',
  SHIP_TO_BUYER_REQUEST_DATE = 'SHIP_TO_BUYER_REQUEST_DATE',
  SHIP_DATE = 'SHIP_DATE',
}

enum RequestTab {
  ALL = 'ALL',
  NOT_SHIPPED = 'NOT_SHIPPED',
  IN_TRANSIT = 'IN_TRANSIT',
  WAITING_STOCK = 'WAITING_STOCK',
  IN_STOCK = 'IN_STOCK',
  ITEM_SHIP = 'ITEM_SHIP',
  ITEM_DISPOSAL = 'ITEM_DISPOSAL',
  COMPLETE = 'COMPLETE',
}

const ItemManagementTab = [
  RequestTab.ALL,
  RequestTab.NOT_SHIPPED,
  RequestTab.IN_TRANSIT,
  RequestTab.WAITING_STOCK,
  RequestTab.IN_STOCK,
  RequestTab.ITEM_SHIP,
  RequestTab.ITEM_DISPOSAL,
  RequestTab.COMPLETE,
];

type TPagination = {
  page: number;
  pageSize: number;
  total: number;
};
type TSearch = {
  from: string | undefined;
  to: string | undefined;
  fullTextSearch: string | undefined;
  dateType: string | undefined;
  checkbox: boolean;
};
type TDataTable = {
  form: FormInstance;
  isLoadingItems: boolean;
  storageItems: StorageItem[];
  paginationInfo: TPagination;
  tabActive: string;
  searchItem: TSearch;
  setPagination: (paginationInfo: TPagination) => void;
  copyToClipboard: (value: string) => void;
  handleSearch: (value: string) => void;
  setSearchItem: (searchItem: TSearch) => void;
  handleKeyDown: (e: any) => void;
  disabledDateStart: (current: any) => boolean;
  disabledDateEnd: (current: any) => boolean;
  sort: TSort;
  fakeSearch: string;
};
const n = (key: keyof TSearch) => key;

const getColumn = (data: TColumnTable): any => {
  const { intl, tabActive, copyToClipboard, navigate, sort } = data;
  const showTrackingNumber = ['IN_TRANSIT', 'WAITING_STOCK'].includes(tabActive);
  return [
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      width: '100px',
      render: (_, record: StorageItem) => (
        <ButtonDetail
          content={intl.formatMessage({ id: 'item.detail' }) + ' '}
          buttonProps={{
            onClick: (e) => {
              if (e.ctrlKey || e.metaKey) {
                window.open(`${ADMIN_ROUTE_PATH.ITEM_MANAGEMENT_DETAIL}/${record?.id}`, '_blank');
              } else {
                navigate(`${ADMIN_ROUTE_PATH.ITEM_MANAGEMENT_DETAIL}/${record?.id}`);
              }
            },
          }}
        />
      ),
    },
    {
      title: () => {
        const columns: TColumnSort = 'internalSku';
        return (
          <div className="item-management__table-sort" onClick={() => sort.handleSort(columns)}>
            <div>BayPack管理番号</div>
            {sort.filterSort.columns === columns ? (
              <span className={`${sort.filterSort.sort === 'DESC' ? 'rotate-reverse' : ''}`}>
                <SvgArrowDown />
              </span>
            ) : (
              <span>
                <SvgFilterSort />
              </span>
            )}
          </div>
        );
      },
      dataIndex: 'sku',
      key: 'sku',
      width: '168px',
      render: (_, record: StorageItem) => (
        <button
          className="item-management__button-copy"
          onClick={() => {
            copyToClipboard(record?.internalSku);
          }}
        >
          <SvgCoppy />
          <p className="item-management__table-text">{record?.internalSku}</p>
        </button>
      ),
    },
    {
      title: intl.formatMessage({ id: 'item.customer' }),
      dataIndex: 'customer',
      key: 'customer',
      className: 'col_customer',
      width: '136px',
      render: (_, record: StorageItem) => (
        <p className="item-management__table-text">
          <p className="item-management__table-text text-1-line">{record?.storageRequest?.user?.name}</p>
          <span>{record?.storageRequest?.user?.userCode}</span>
        </p>
      ),
    },
    {
      title: intl.formatMessage({ id: 'item.productName' }),
      dataIndex: 'productName',
      key: 'productName',
      width: ['WAITING_STOCK', 'IN_TRANSIT'].includes(tabActive) ? '516px' : '719px',
      render: (_, record: StorageItem) => (
        <p className="item-management__table-text text-2-line">{decodeHtml(record?.itemName || '')}</p>
      ),
    },
    {
      title: intl.formatMessage({ id: 'item.situation' }),
      dataIndex: 'status',
      key: 'status',
      width: '200px',
      render: (_, record: StorageItem) => (
        <>
          <p className="item-management__table-text">
            {intl.formatMessage({ id: `item.${handleViewStatusItem(renderStatus(record), true)}` })}
            {record.status === STATUS_ITEM_MANAGEMENT.IN_STORAGE && record.storageItemRack?.rack?.code
              ? `_${record.storageItemRack?.rack?.code}`
              : ''}
            {record?.shippoShippingLabel?.eta && tabActive === RequestTab.IN_TRANSIT
              ? ` : ${moment(record?.shippoShippingLabel?.eta).tz(AmericaTimezone).format(TIME_FORMAT.DATE('/'))}`
              : ''}
          </p>
          {tabActive === RequestTab.IN_TRANSIT && (
            <p className="item-management__table-text status-tracking">
              {record?.shippoShippingLabel?.trackingStatusDetails || ''}
            </p>
          )}
        </>
      ),
    },
    showTrackingNumber && {
      title: intl.formatMessage({ id: 'item.tracking_number' }),
      key: 'trackingNumber',
      className: 'item-management__table-text',
      width: 260,
      render: (record) => {
        const tracking = record?.shippoShippingLabel?.trackingNumber ?? record?.storageRequest?.trackingNumberProvided;

        const formatTracking = (str) => {
          if (!str) return '';
          const part1 = str.slice(0, 10);
          const part2 = str.slice(10, 14);
          const part3 = str.slice(14);
          return `${part1} ${part2} ${part3}`.trim();
        };

        return <p>{formatTracking(tracking)}</p>;
      },
    },
    {
      title: () => {
        const columns: TColumnSort = 'createdAt';
        const shipToBuyerRequestCreatedAtColumn: TColumnSort = 'lastUpdate';
        const storageRequestShippedAt: TColumnSort = 'storageRequestShippedAt';
        const id =
          tabActive === RequestTab.ITEM_SHIP
            ? 'item.shipmentRequestedDate'
            : tabActive === RequestTab.ITEM_DISPOSAL
              ? 'item.disposalRequestedDate'
              : tabActive === RequestTab.COMPLETE
                ? 'item.shipToBuyerRequestCreatedAt'
                : tabActive === RequestTab.IN_TRANSIT
                  ? 'item.dateBuyerShip'
                  : 'item.requestedDate';
        const sortColumn =
          tabActive === RequestTab.COMPLETE
            ? shipToBuyerRequestCreatedAtColumn
            : tabActive === RequestTab.IN_TRANSIT
              ? storageRequestShippedAt
              : columns;
        return (
          <>
            {['item.requestedDate', 'item.shipToBuyerRequestCreatedAt', 'item.dateBuyerShip'].includes(id) ? (
              <div className="item-management__table-sort" onClick={() => sort.handleSort(sortColumn)}>
                <div>{intl.formatMessage({ id })}</div>
                {sort.filterSort.columns === sortColumn ? (
                  <span className={`${sort.filterSort.sort === 'DESC' ? 'rotate-reverse' : ''}`}>
                    <SvgArrowDown />
                  </span>
                ) : (
                  <span>
                    <SvgFilterSort />
                  </span>
                )}
              </div>
            ) : (
              intl.formatMessage({ id })
            )}
          </>
        );
      },
      dataIndex: 'requestedDate',
      key: 'requestedDate',
      width: '220px',
      render: (_, record: StorageItem) => {
        const requestDateShip = record.shipToBuyerItem?.shipToBuyerRequest?.createdAt
          ? formatDate(record.shipToBuyerItem?.shipToBuyerRequest?.createdAt ?? '')
          : record.shipBackSellerRequest?.createdAt
            ? formatDate(record.shipBackSellerRequest?.createdAt)
            : '';
        const dateBuyerShip = record?.storageRequest?.shippedAt
          ? moment(record?.storageRequest?.shippedAt).tz(AmericaTimezone).format(TIME_FORMAT.DATE('/'))
          : '';
        const dateDisposal = record?.disposalRequest?.createdAt ? formatDate(record?.disposalRequest?.createdAt) : '';
        const shipToBuyerItemCreatedAt = formatDate(record?.['lastUpdate']);
        const requestDate =
          tabActive === RequestTab.ITEM_SHIP
            ? requestDateShip
            : tabActive === RequestTab.COMPLETE
              ? shipToBuyerItemCreatedAt
              : tabActive === RequestTab.IN_TRANSIT
                ? dateBuyerShip
                : tabActive === RequestTab.ITEM_DISPOSAL
                  ? dateDisposal
                  : formatDate(record?.storageRequest?.createdAt ?? '');
        return <p className="item-management__table-text">{requestDate}</p>;
      },
    },
  ].filter(Boolean);
};

const TableAndFields = ({ ...props }: TDataTable) => {
  const {
    form,
    isLoadingItems,
    storageItems,
    paginationInfo,
    tabActive,
    searchItem,
    setPagination,
    copyToClipboard,
    setSearchItem,
    handleSearch,
    handleKeyDown,
    disabledDateStart,
    disabledDateEnd,
    sort,
    fakeSearch,
  } = props;
  const intl = useIntl();
  const navigate = useNavigate();

  const tabOptions = {
    ALL: [0, 1, 2, 3, 4],
    NOT_SHIPPED: [0],
    IN_TRANSIT: [0],
    WAITING_STOCK: [0, 1],
    IN_STOCK: [0, 1, 2],
    ITEM_SHIP: [0, 1, 2, 3],
    ITEM_DISPOSAL: [0, 1, 2],
    COMPLETE: [0, 1, 2, 3, 4],
  };

  const filterOptionsByTab = () => {
    const indices = tabOptions[tabActive] || [];
    return mapOption.filter((option, index) => indices.includes(index));
  };
  const mapOption: any = [
    {
      value: DataType.NEW_REQUEST_DATE,
      label: (
        <div className="h-100 d-flex align-items-center">
          <span className="font-base font-weight-500 font-size-16 color-514B4D" style={{ color: '#514B4D' }}>
            新規依頼日
          </span>
          {searchItem.dateType === DataType.NEW_REQUEST_DATE && <CheckIcon />}
        </div>
      ),
    },
    {
      value: DataType.ARRIVAL_DATE,
      label: (
        <div className="h-100 d-flex align-items-center">
          <span className="font-base font-weight-500 font-size-16 color-514B4D" style={{ color: '#514B4D' }}>
            到着日
          </span>
          {searchItem.dateType === DataType.ARRIVAL_DATE && <CheckIcon />}
        </div>
      ),
    },
    {
      value: DataType.INSPECTION_DATE,
      label: (
        <div className="h-100 d-flex align-items-center">
          <span className="font-base font-weight-500 font-size-16 color-514B4D" style={{ color: '#514B4D' }}>
            検品日
          </span>
          {searchItem.dateType === DataType.INSPECTION_DATE && <CheckIcon />}
        </div>
      ),
    },
    {
      value: DataType.SHIP_TO_BUYER_REQUEST_DATE,
      label: (
        <div className="h-100 d-flex align-items-center">
          <span className="font-base font-weight-500 font-size-16 color-514B4D" style={{ color: '#514B4D' }}>
            発送依頼日
          </span>
          {searchItem.dateType === DataType.SHIP_TO_BUYER_REQUEST_DATE && <CheckIcon />}
        </div>
      ),
    },
    {
      value: DataType.SHIP_DATE,
      label: (
        <div className="h-100 d-flex align-items-center">
          <span className="font-base font-weight-500 font-size-16 color-514B4D" style={{ color: '#514B4D' }}>
            発送日
          </span>
          {searchItem.dateType === DataType.SHIP_DATE && <CheckIcon />}
        </div>
      ),
    },
  ];
  const handleCheckboxChange = (e) => {
    setSearchItem({
      ...searchItem,
      checkbox: e,
    });
    setPagination({
      pageSize: 20,
      page: 1,
      total: 0,
    });
  };
  return (
    <>
      <div className="d-flex align-items-center justify-content-start">
        <Form form={form} className="d-flex align-items-center">
          <div className="d-flex align-items-center gap-8 select-option-day">
            <Form.Item name={n('dateType')} className="m-b-0">
              <CustomSelect
                defaultValue={DataType.NEW_REQUEST_DATE}
                options={filterOptionsByTab()}
                value={searchItem.dateType}
                onChange={(value) => setSearchItem({ ...searchItem, dateType: value })}
              />
            </Form.Item>
            <span>:</span>
          </div>
          <div className="d-flex align-items-center m-r-80 gap-8">
            <div className="d-flex align-items-center gap-2">
              <Form.Item name={n('from')} className="m-b-0">
                <DatepickerCustom
                  datePickerProp={{
                    onChange: (_, dateString: string) => {
                      setSearchItem({ ...searchItem, from: dateString ? (dateString as string) : undefined });
                    },
                    disabledDate: disabledDateStart,
                  }}
                />
              </Form.Item>
              <span className="item-management__range-date">~</span>
              <Form.Item name={n('to')} className="m-b-0">
                <DatepickerCustom
                  datePickerProp={{
                    onChange: (_, dateString: string) => {
                      setSearchItem({ ...searchItem, to: dateString ? (dateString as string) : undefined });
                    },
                    disabledDate: disabledDateEnd,
                  }}
                />
              </Form.Item>
            </div>
          </div>
          <Form.Item name={n('checkbox')} className="m-b-0" valuePropName="checked">
            {['COMPLETE'].includes(tabActive) && (
              <div className="request-management_filter_checkbox">
                <CheckboxCustom
                  checked={searchItem.checkbox}
                  onChange={handleCheckboxChange}
                  content={intl.formatMessage({ id: 'item.checkBoxComplete' })}
                />
              </div>
            )}
          </Form.Item>
          <Form.Item name={n('fullTextSearch')} className="m-b-0">
            <CustomSearchInput
              className="font-weight-500 font-size-16 switch-false item-management__search-input"
              placeholder={intl.formatMessage({ id: 'item.searchPlaceholder' })}
              value={fakeSearch}
              onChange={({ target: { value } }) => {
                handleSearch(value);
              }}
              onKeyDown={handleKeyDown}
            />
          </Form.Item>
        </Form>
      </div>
      <div className="m-t-24">
        <TableWrap
          data={storageItems}
          isLoading={isLoadingItems}
          className="item-management__table ebay__scroll-bar-custom-horizon"
          columns={getColumn({ intl, tabActive, copyToClipboard, navigate, sort })}
          isScroll
          scrollValue={{ y: '54vh' }}
        />
      </div>
      <div className="item-management__table-pagination-container">
        <Pagination
          current={paginationInfo.page}
          pageSize={paginationInfo.pageSize}
          total={paginationInfo.total}
          showSizeChanger
          pageSizeOptions={[20, 50, 100]}
          locale={{ items_per_page: intl.formatMessage({ id: 'item.lines' }) }}
          onChange={(current: number, pageSize: number) => {
            setPagination({ ...paginationInfo, page: current, pageSize });
          }}
        />
      </div>
    </>
  );
};
const handleGetCountOfTab = (status: string, dataCount: CountForItemListResponse) => {
  if (!!dataCount) {
    switch (status) {
      case RequestTab.NOT_SHIPPED:
        return dataCount.notShipped;
      case RequestTab.IN_TRANSIT:
        return dataCount.inTransit;
      case RequestTab.WAITING_STOCK:
        return dataCount.waitingStock;
      case RequestTab.IN_STOCK:
        return dataCount.inStock;
      case RequestTab.ITEM_SHIP:
        return dataCount.itemShip;
      case RequestTab.ITEM_DISPOSAL:
        return dataCount.itemDisposal;

      default:
        return '';
    }
  } else {
    return '';
  }
};
const ItemManagement: React.FC = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [tabActive, setTabActive] = useState<string>(searchParams.get('tab') ?? RequestTab.ALL);
  const [listItems, setListItems] = useState<StorageItem[]>([]);
  const [pagination, setPagination] = useState<TPagination>({
    pageSize: 20,
    page: 1,
    total: 0,
  });
  const [searchItem, setSearchItem] = useState<TSearch>({
    from: undefined,
    to: undefined,
    fullTextSearch: undefined,
    dateType: DataType.NEW_REQUEST_DATE,
    checkbox: false,
  });
  const [openCsvModal, setOpenCsvModal] = useState(false);
  const [filterSort, setFilterSort] = useState<TFilterSort>({ columns: null, sort: null });
  const [searchText, setSearchText] = useState<string>('');
  const refDebounceSearch = useRef<ReturnType<typeof setTimeout> | null>(null);

  const authUser = useSelector((state: RootState) => state.auth).authUser;

  const { data: countTab, refetch: refetchCountTab } = useQuery({
    queryKey: [QUERY_KEY.ITEMS_COUNT],
    queryFn: () => storageItemApi.storageItemControllerCountForItemList(),
    staleTime: 1000,
  });

  const getTypeSort = (col: TColumnSort): TTypeSort =>
    filterSort.columns !== col ? 'DESC' : filterSort.sort === null ? 'DESC' : filterSort.sort === 'DESC' ? 'ASC' : null;
  const handleSort = (columns: TColumnSort, sort = getTypeSort(columns)) => {
    const sortNew: TFilterSort = { columns, sort };
    const sortNull: TFilterSort = { columns: null, sort: null };
    setFilterSort(sort ? sortNew : sortNull);
  };

  const {
    data: listItemsManagement,
    isLoading: isLoadingItems,
    refetch: refetchListItems,
  } = useQuery({
    queryKey: [
      QUERY_KEY.LIST_GROUP_CHAT,
      tabActive,
      pagination.pageSize,
      pagination.page,
      searchItem.fullTextSearch,
      searchItem.from || searchItem.to ? searchItem.dateType : null,
      searchItem.from,
      searchItem.to,
      searchItem.checkbox,
      filterSort,
    ],
    queryFn: () =>
      storageItemApi.storageItemControllerItemManagementAdmin(
        pagination.page,
        pagination.pageSize,
        filterSort.columns && filterSort.sort
          ? `${filterSort.columns}-${filterSort.sort}`
          : RequestTab.ITEM_SHIP === tabActive
            ? 'shipToBuyerRequestCreatedAt-ASC'
            : RequestTab.ITEM_DISPOSAL === tabActive
              ? 'disposalRequestCreatedAt-DESC'
              : null,
        searchItem.fullTextSearch,
        searchItem.dateType as DataType,
        searchItem.from,
        searchItem.to,
        undefined,
        tabActive as RequestTab,
        searchItem.checkbox,
      ),
    staleTime: 1000,
  });

  useEffect(() => {
    if (searchParams && searchParams.get('tab')) {
      setTabActive(searchParams.get('tab'));
    } else {
      setTabActive(RequestTab.ALL);
    }
  }, [searchParams]);

  useEffect(() => {
    if (listItemsManagement?.data?.content) {
      const listRacksData: StorageItem[] = listItemsManagement.data.content;
      const total: number = listItemsManagement.data.total;
      setListItems(listRacksData ?? []);
      if (total !== pagination.total) {
        setPagination({ ...pagination, total });
      }
    }
  }, [listItemsManagement]);

  useEffect(() => {
    if (refDebounceSearch.current) clearTimeout(refDebounceSearch.current);

    refDebounceSearch.current = setTimeout(() => {
      const v = searchText.trim();

      setPagination((prev) => ({ ...prev, page: 1 }));
      setSearchItem((prev) => ({
        ...prev,
        fullTextSearch: v || undefined,
      }));
    }, 1000);

    return () => {
      if (refDebounceSearch.current) clearTimeout(refDebounceSearch.current);
    };
  }, [searchText]);

  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value).then(
      () => {
        NotificationSuccess({ content: intl.formatMessage({ id: 'common.copy' }) });
      },
      (err) => {
        NotificationSuccess({ content: err });
      },
    );
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const applySearch = useCallback((value: string) => {
    const v = value.trim();

    setPagination((prev) => ({ ...prev, page: 1 }));
    setSearchItem((prev) => ({
      ...prev,
      fullTextSearch: v || undefined,
    }));
  }, []);

  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (refDebounceSearch.current) clearTimeout(refDebounceSearch.current);
      applySearch(searchText);
    }
  };

  const disabledDateEnd = (current) => {
    if (!!searchItem.from) {
      return current && current < dayjs(searchItem.from).startOf('day');
    } else {
      return undefined;
    }
  };
  const disabledDateStart = (current) => {
    if (!!searchItem.to) {
      return current && current > dayjs(searchItem.to).startOf('day');
    } else {
      return undefined;
    }
  };
  const exportCsv = useMutation({
    mutationKey: ['exportCsv'],
    mutationFn: () => fileExportApi.fileExportControllerExportCsv(tabActive as any, { responseType: 'blob' }),
    onSuccess: ({ data }) => {
      const fileName = dayjs().format(TIME_FORMAT.MESSAGE_TIME);
      saveAs(data as any, `${intl.formatMessage({ id: 'item.title' })}_${fileName.replace(' ', '_')}.csv`);
    },
  });
  const handleDownload = () => exportCsv.mutate();
  const items = ItemManagementTab.map((item, index) => {
    return {
      label: (
        <p className="item-management__title-tabs">
          <span className="item-management__title-name">{intl.formatMessage({ id: `item.${item}` })}</span>
          <span className="item-management__title-number small-number">
            {handleGetCountOfTab(item, countTab?.data)}
          </span>
        </p>
      ),
      key: item,
      disabled: false,
      children: (
        <TableAndFields
          key={item}
          form={form}
          isLoadingItems={isLoadingItems}
          storageItems={listItems}
          paginationInfo={pagination}
          tabActive={tabActive}
          searchItem={searchItem}
          setPagination={setPagination}
          copyToClipboard={copyToClipboard}
          setSearchItem={setSearchItem}
          handleKeyDown={handleKeyDown}
          disabledDateStart={disabledDateStart}
          disabledDateEnd={disabledDateEnd}
          sort={{ filterSort, handleSort }}
          fakeSearch={searchText}
          handleSearch={handleSearch}
        />
      ),
    };
  });
  return (
    <div className="item-management">
      <div className="item-management__header">
        <span className="color-title font-weight-600 font-size-26">{intl.formatMessage({ id: 'item.title' })}</span>
        <div className="right">
          <div className="item-management__btn-download">
            <DowloadButton onClick={handleDownload} />
            {exportCsv.isPending && (
              <div className="loading">
                <Spin />
              </div>
            )}
          </div>
          {authUser?.role == 'administrator' && (
            <div className="item-management__btn-import">
              <Button onClick={() => setOpenCsvModal(true)}>
                <SvgImport />
                一括登録
              </Button>
            </div>
          )}
        </div>
      </div>

      <Tabs
        activeKey={tabActive}
        // defaultActiveKey={searchParams.get('tab') || RequestTab.ALL}
        // centered
        animated={{ inkBar: true, tabPane: false }}
        tabPosition={'top'}
        onChange={(e: string) => {
          setTabActive(e);
          setSearchParams({ ['tab']: e });
          setSearchText('');
          setPagination({
            pageSize: 20,
            page: 1,
            total: 0,
          });

          setSearchItem({
            to: undefined,
            from: undefined,
            fullTextSearch: undefined,
            dateType: DataType.NEW_REQUEST_DATE,
            checkbox: false,
          });
          form.resetFields();
          refetchCountTab();
        }}
        items={items}
      />

      <CsvBulkRegisterModal
        isOpen={openCsvModal}
        onCancel={() => {
          setOpenCsvModal(false);
          refetchListItems();
          refetchCountTab();
        }}
        onSuccess={() => {
          refetchListItems();
          refetchCountTab();
        }}
      />
    </div>
  );
};

export default ItemManagement;
